import { ActivityTypeEnum, OfferInterface } from '@sport-activities/sdk';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LocaleService } from '~/i18n/services/locale.service';
import { Subscription } from 'rxjs';
import { format, isSameDay } from 'date-fns';

import { Activity } from '~/core/model/activity.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { BadgeVariantEnum } from '~/component/badge/enums/variant.enum';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { SubscriptionSearchService } from '~/attendee/service/subscription-search.service';
import { Timeslot } from '~/core/model/timeslot.model';
import { TimeslotListVariantEnum } from '~/component/activity-timeslot-list/enums/timeslot-list-variant.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { thresholdCalculator } from '~/core/helpers/threshold/threshold-calculator.helper';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import isBulgaria from '~/core/helpers/country/country.helper';
import { appConfig } from '~/app.config';

@Component({
  selector: 'app-attendee-activity-offers',
  templateUrl: './offers.component.html',
})
export class AttendeeActivityOffersComponent implements OnInit, OnDestroy {
  @Input() public activity: Activity;
  @Input() public hasSeeMoreUrl = false;
  @Input() public timeslotVariant: TimeslotListVariantEnum;
  @Input() public timeslots: Timeslot[];

  private readonly subscriptions = new Subscription();

  private page = 1;

  public readonly badgeGreyVariant = BadgeVariantEnum.GREY_LIGHT;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly modalVariant: ModalVariantEnum = ModalVariantEnum.Full;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;

  public hasLoadMoreButton: boolean;
  public hasPagination: boolean;
  public isMobileDevice: boolean;
  public isOfferPanelOpen = false;
  public offer: OfferInterface;
  public offers: OfferInterface[];
  public selectedTimeslot: Timeslot;
  public isBg: boolean;

  constructor(
    private readonly checkoutService: CheckoutService,
    private readonly localeService: LocaleService,
    private readonly matchMediaService: MatchMediaService,
    private readonly redirectionService: RedirectionService,
    public readonly activityService: ActivityService,
    public readonly subscriptionSearchService: SubscriptionSearchService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.isBg = isBulgaria();
    this.hasPagination = !this.hasSeeMoreUrl && this.timeslots?.length > appConfig.numberOfTimeslotsToDisplay;
    this.hasLoadMoreButton = this.hasPagination;

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );
  }

  private get urlRedirection(): string {
    return this.activity.type === ('BIRTHDAY' as ActivityTypeEnum) ||
      this.activity.type === ('PRODUCT_TEST' as ActivityTypeEnum)
      ? '/checkout.birthday-sport'
      : '/checkout.subscription';
  }

  public get capacityClass(): string {
    if (!this.selectedTimeslot) {
      return '';
    }
    const remainingCapacityPercentage =
      (this.selectedTimeslot.remainingAttendeeCapacity /
        this.selectedTimeslot.maximumAttendeeCapacity) *
      100;
    return thresholdCalculator(remainingCapacityPercentage, [
      {
        threshold: 0,
        value: 'text-vtmn-play-status-negative',
      },
      {
        threshold: 15,
        value: 'text-vtmn-play-status-warning',
      },
      {
        threshold: 60,
        value: 'text-vtmn-play-status-positive',
      },
    ]);
  }

  public get formattedTimeslotDate(): string {
    if (!this.selectedTimeslot) {
      return '';
    }
    const startDate = new Date(this.selectedTimeslot.startDate);
    const endDate = new Date(this.selectedTimeslot.endDate);
    const fullFormat = 'EEE dd MMM HH:mm';
    const shortFormat = 'HH:mm';
    const formatOptions = {
      locale: this.localeService.dateLocale,
    };
    return `${format(startDate, fullFormat, formatOptions)} - ${format(
      endDate,
      isSameDay(startDate, endDate) ? shortFormat : fullFormat,
      formatOptions
    )}`;
  }

  public get hasLocalityInformation(): boolean {
    return !!(
      this.activity?.location?.address?.postalCode ||
      this.activity?.location?.address?.addressLocality
    );
  }

  public get hasStreetAddress(): boolean {
    return !!this.activity?.location?.address?.streetAddress;
  }

  public get path(): string {
    return this.activity.isLive
      ? '/attendee.show-live-activity-dates'
      : '/attendee.show-activity-dates';
  }

  public closePanel(): void {
    this.isOfferPanelOpen = false;
  }

  public loadMore(): void {
    this.page += 1;

    this.activityService
      .getActivityTimeslots(
        this.activity?.sku?.toString(),
        this.subscriptionSearchService.getTimeslotsFilters(),
        this.page,
        20
      )
      .subscribe((timeslots) => {
        this.timeslots = this.timeslots?.concat(timeslots);
        this.hasLoadMoreButton = timeslots.length === 20;
      });
  }

  public reloadTimeslots(): void {
    this.activityService
      .getActivityTimeslots(
        this.activity?.sku?.toString(),
        this.subscriptionSearchService.getTimeslotsFilters(),
        this.page,
        20
      )
      .subscribe((timeslots) => {
        this.timeslots = timeslots;
      });
  }

  public onValidateSelection(): void {
    this.checkoutService.resetCheckout();
    this.redirectionService.redirect(this.urlRedirection, {
      queryParams: {
        sku: this.selectedTimeslot.identifier,
        offer: this.offer?.identifier,
      },
    });
  }

  public selectOffer(offer: OfferInterface): void {
    this.offer = offer;
  }

  public selectTimeslot(timeslot: Timeslot): void {
    this.selectedTimeslot = timeslot;
    this.offers = timeslot.offers;

    if (this.offers.length === 1) {
      this.checkoutService.resetCheckout();
      this.redirectionService.redirect(this.urlRedirection, {
        queryParams: {
          sku: timeslot.identifier,
          offer: timeslot.offers[0]?.identifier,
        },
      });

      return;
    }

    this.isOfferPanelOpen = true;
  }

  deleteTimeslot(timeslot) {
    this.checkoutService.deleteCartItem(timeslot);
    this.reloadTimeslots();
  }
}
